/**
 * Quiz History Management
 *
 * Tracks quiz attempts, scores, and performance over time
 * Stored in localStorage for persistence
 */

const STORAGE_KEY = 'sifiso-quiz-history';

/**
 * Get quiz history from localStorage
 * @returns {Array} Array of quiz attempt records
 */
export function getQuizHistory() {
  try {
    const history = localStorage.getItem(STORAGE_KEY);
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('Error loading quiz history:', error);
    return [];
  }
}

/**
 * Save a quiz attempt to history
 * @param {Object} attempt - Quiz attempt data
 */
export function saveQuizAttempt(attempt) {
  try {
    const history = getQuizHistory();
    const newAttempt = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      ...attempt
    };

    history.unshift(newAttempt); // Add to beginning of array

    // Keep only last 100 attempts to prevent localStorage overflow
    const trimmedHistory = history.slice(0, 100);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedHistory));
    return newAttempt;
  } catch (error) {
    console.error('Error saving quiz attempt:', error);
    return null;
  }
}

/**
 * Get history for a specific category
 * @param {string} categoryId - Category ID
 * @returns {Array} Filtered quiz attempts
 */
export function getCategoryHistory(categoryId) {
  const history = getQuizHistory();
  return history.filter(attempt => attempt.categoryId === categoryId);
}

/**
 * Get history for a specific category and difficulty
 * @param {string} categoryId - Category ID
 * @param {string} difficulty - Difficulty level
 * @returns {Array} Filtered quiz attempts
 */
export function getCategoryDifficultyHistory(categoryId, difficulty) {
  const history = getQuizHistory();
  return history.filter(
    attempt => attempt.categoryId === categoryId && attempt.difficulty === difficulty
  );
}

/**
 * Get best score for a category/difficulty combination
 * @param {string} categoryId - Category ID
 * @param {string} difficulty - Difficulty level
 * @returns {Object|null} Best attempt or null
 */
export function getBestScore(categoryId, difficulty) {
  const attempts = getCategoryDifficultyHistory(categoryId, difficulty);
  if (attempts.length === 0) return null;

  return attempts.reduce((best, current) => {
    const currentPercentage = (current.score / current.totalQuestions) * 100;
    const bestPercentage = (best.score / best.totalQuestions) * 100;
    return currentPercentage > bestPercentage ? current : best;
  });
}

/**
 * Get average score for a category/difficulty combination
 * @param {string} categoryId - Category ID
 * @param {string} difficulty - Difficulty level
 * @returns {number} Average score percentage or 0
 */
export function getAverageScore(categoryId, difficulty) {
  const attempts = getCategoryDifficultyHistory(categoryId, difficulty);
  if (attempts.length === 0) return 0;

  const totalPercentage = attempts.reduce((sum, attempt) => {
    return sum + ((attempt.score / attempt.totalQuestions) * 100);
  }, 0);

  return Math.round(totalPercentage / attempts.length);
}

/**
 * Get total number of quizzes completed
 * @returns {number} Total quiz count
 */
export function getTotalQuizzesCompleted() {
  return getQuizHistory().length;
}

/**
 * Get statistics for all categories
 * @returns {Object} Category statistics
 */
export function getCategoryStats() {
  const history = getQuizHistory();
  const stats = {};

  history.forEach(attempt => {
    if (!stats[attempt.categoryId]) {
      stats[attempt.categoryId] = {
        totalAttempts: 0,
        totalScore: 0,
        totalQuestions: 0,
        difficulties: {
          easy: { attempts: 0, bestScore: 0 },
          medium: { attempts: 0, bestScore: 0 },
          hard: { attempts: 0, bestScore: 0 }
        }
      };
    }

    const catStat = stats[attempt.categoryId];
    catStat.totalAttempts++;
    catStat.totalScore += attempt.score;
    catStat.totalQuestions += attempt.totalQuestions;

    const diffStat = catStat.difficulties[attempt.difficulty];
    if (diffStat) {
      diffStat.attempts++;
      const percentage = (attempt.score / attempt.totalQuestions) * 100;
      diffStat.bestScore = Math.max(diffStat.bestScore, percentage);
    }
  });

  return stats;
}

/**
 * Get questions that user got wrong (for spaced repetition)
 * @returns {Array} Questions that need review
 */
export function getQuestionsForReview() {
  const history = getQuizHistory();
  const wrongQuestions = [];

  history.forEach(attempt => {
    if (attempt.answeredQuestions) {
      attempt.answeredQuestions.forEach(question => {
        if (!question.isCorrect) {
          wrongQuestions.push({
            ...question,
            attemptDate: attempt.timestamp,
            categoryId: attempt.categoryId,
            difficulty: attempt.difficulty
          });
        }
      });
    }
  });

  // Remove duplicates (same question ID)
  const uniqueQuestions = wrongQuestions.reduce((acc, current) => {
    const existing = acc.find(q => q.id === current.id);
    if (!existing) {
      acc.push(current);
    }
    return acc;
  }, []);

  return uniqueQuestions;
}

/**
 * Check if user has completed a category at all difficulty levels
 * @param {string} categoryId - Category ID
 * @returns {boolean} True if completed at all difficulties
 */
export function hasCategoryMastery(categoryId) {
  const stats = getCategoryStats();
  const categoryStat = stats[categoryId];

  if (!categoryStat) return false;

  return (
    categoryStat.difficulties.easy.attempts > 0 &&
    categoryStat.difficulties.medium.attempts > 0 &&
    categoryStat.difficulties.hard.attempts > 0
  );
}

/**
 * Get user's streak (consecutive days with quiz activity)
 * @returns {number} Current streak in days
 */
export function getCurrentStreak() {
  const history = getQuizHistory();
  if (history.length === 0) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let streak = 0;
  let currentDate = new Date(today);

  // Group attempts by date
  const attemptsByDate = {};
  history.forEach(attempt => {
    const attemptDate = new Date(attempt.timestamp);
    attemptDate.setHours(0, 0, 0, 0);
    const dateKey = attemptDate.toISOString().split('T')[0];
    attemptsByDate[dateKey] = true;
  });

  // Count consecutive days backwards from today
  while (true) {
    const dateKey = currentDate.toISOString().split('T')[0];
    if (attemptsByDate[dateKey]) {
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      break;
    }
  }

  return streak;
}

/**
 * Clear all quiz history (for testing or reset)
 */
export function clearQuizHistory() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing quiz history:', error);
    return false;
  }
}

export default {
  getQuizHistory,
  saveQuizAttempt,
  getCategoryHistory,
  getCategoryDifficultyHistory,
  getBestScore,
  getAverageScore,
  getTotalQuizzesCompleted,
  getCategoryStats,
  getQuestionsForReview,
  hasCategoryMastery,
  getCurrentStreak,
  clearQuizHistory
};
